import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.beneficio-acumulado-cuanto-sacar-restaurante.${field}`

const SOURCES: { href?: string; labelKey: string; fallback: string }[] = [
    {
        labelKey: 'sources.1',
        fallback: 'Porcentaje y plazos del modelo 130, vía guía publicada por Wolters Kluwer',
    },
    {
        labelKey: 'sources.2',
        fallback:
            'Tipos del Impuesto sobre Sociedades 2026 y régimen transitorio de la Ley 7/2024, vía análisis publicado por Wolters Kluwer',
    },
    {
        labelKey: 'sources.3',
        fallback:
            'Ejemplo del trimestre (enero −7.000 €, febrero +3.000 €, marzo +4.750 €): elaboración propia de Tipi, continuación del ejemplo de bar de barrio del artículo sobre beneficio real del mes',
    },
]

export function BeneficioAcumuladoArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'creencia', label: t(k('toc.1'), '1. «Este mes ha ido bien, puedo sacar»') },
        { id: 'trimestre', label: t(k('toc.2'), '2. El mismo bar, tres meses') },
        { id: 'regla', label: t(k('toc.3'), '3. La regla del acumulado') },
        { id: 'escenarios', label: t(k('toc.4'), '4. Qué pasa si sacas los 2.900 €') },
        { id: 'semana', label: t(k('toc.5'), '5. Qué mirar antes de tocar la caja') },
        { id: 'matices', label: t(k('toc.6'), '6. Lo que no cerramos nosotros') },
    ]

    const weekItems = [
        t(
            k('week.1'),
            'Monta las cinco líneas de los últimos seis meses (o del año) y saca el acumulado. Con un Excel vale.',
        ),
        t(
            k('week.2'),
            'Calcula la reserva de impuestos sobre ese acumulado (modelo 130 al 20 % si eres autónomo) y réstala antes de hablar de «sacar».',
        ),
        t(
            k('week.3'),
            'Suma el principal del préstamo de esos meses. Si el acumulado tras impuestos no cubre esa salida, no hay extracción sana.',
        ),
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Marzo puede dar 4.750 € de resultado y 2.900 € «tuyos». El mismo bar cierra el trimestre con 750 € de beneficio y 2.100 € menos en la cuenta que el 1 de enero. El mes no decide. Decide el acumulado.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(k('heroAlt'), 'Hostelero revisando números del trimestre en una mesa del bar')}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <nav className={articleUi.toc} aria-label={t(k('tocAria'), 'Índice de contenidos')}>
                    <p className={articleUi.tocLabel}>{t(k('tocLabel'), 'Índice')}</p>
                    <ol className={articleUi.tocList}>
                        {tocItems.map(item => (
                            <li key={item.id}>
                                <a href={`#${item.id}`}>{item.label}</a>
                            </li>
                        ))}
                    </ol>
                </nav>

                <h2 id='creencia' className={articleUi.h2}>
                    {t(k('h2.creencia'), '«Este mes ha ido bien, puedo sacar»')}
                </h2>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.creencia.1'),
                            'Es la frase que más se oye cuando marzo o julio salvan la cara. Tiene una parte cierta: el mes ha ganado. La parte falsa es creer que eso borra enero.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(k('p.creencia.2Before'), 'En ')}
                        <LearnArticleLink slug='beneficio-real-restaurante-cuanto-puedo-sacar'>
                            {t(
                                k('p.creencia.2Link'),
                                'cómo calcular el beneficio real del mes y cuánto de ese dinero puedes sacar',
                            )}
                        </LearnArticleLink>
                        {t(
                            k('p.creencia.2After'),
                            ' vimos que un bar con 4.750 € de resultado en marzo solo puede sacar 2.900 € si miras ese mes solo. Aquí miramos qué pasa cuando el año ya venía flojo.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                        {t(
                            k('p.creencia.3'),
                            'La creencia respetable es «el mes bueno me deja respirar». La regla útil es otra: mientras el acumulado del año no esté en positivo después de reservar impuestos, no sacas nada.',
                        )}
                    </p>
                </div>

                <h2 id='trimestre' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                    {t(k('h2.trimestre'), 'El mismo bar, tres meses')}
                </h2>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.trimestre.1'),
                            'Seguimos el mismo ejemplo: bar de barrio, autónomo, tres empleados. Continuación del mes de marzo del artículo anterior. Cifras de ejemplo nuestras, no de un local concreto.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(k('p.trimestre.2Before'), 'Ese bar hizo ')}
                        <strong>{t(k('p.trimestre.2Strong1'), '−7.000 € en enero')}</strong>
                        {t(k('p.trimestre.2Mid'), ' y ')}
                        <strong>{t(k('p.trimestre.2Strong2'), '+3.000 € en febrero')}</strong>
                        {t(k('p.trimestre.2After'), '.')}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(k('p.trimestre.3Before'), 'Con los 4.750 € de marzo, el trimestre cierra en ')}
                        <strong>{t(k('p.trimestre.3Strong'), '+750 € de beneficio')}</strong>
                        {t(k('p.trimestre.3After'), '.')}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.trimestre.4Before'),
                            'El modelo 130 se calcula sobre el acumulado, así que la reserva de impuestos del trimestre es de 150 €. Y el principal del préstamo ha salido tres veces: ',
                        )}
                        <strong>{t(k('p.trimestre.4Strong'), '2.700 €')}</strong>
                        {t(k('p.trimestre.4After'), '.')}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                        <strong>
                            {t(
                                k('p.trimestre.5'),
                                'El trimestre cierra con 750 € de beneficio y 2.100 € menos en la cuenta que el 1 de enero.',
                            )}
                        </strong>
                    </p>
                </div>

                <h2 id='regla' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                    {t(k('h2.regla'), 'La regla del acumulado')}
                </h2>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(k('p.regla.1Before'), 'La regla entera: ')}
                        <strong>
                            {t(
                                k('p.regla.1Strong'),
                                'mientras el acumulado del año no esté en positivo después de reservar impuestos, no sacas nada.',
                            )}
                        </strong>{' '}
                        {t(k('p.regla.1After'), 'Coger dinero antes no te lo llevas: agranda el agujero que ya había.')}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                        {t(k('p.regla.2Before'), 'Es la misma lógica que en ')}
                        <LearnArticleLink slug='cada-cuanto-revisar-numeros-restaurante'>
                            {t(k('p.regla.2Link'), 'cada cuánto hay que mirar los números del restaurante')}
                        </LearnArticleLink>
                        {t(k('p.regla.2After'), ': el mes bueno no compensa lo que ya perdiste, solo lo tapa un rato.')}
                    </p>
                </div>

                <h2 id='escenarios' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                    {t(k('h2.escenarios'), 'Qué pasa si sacas los 2.900 €')}
                </h2>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.escenarios.1'),
                            'Si en marzo sacas los 2.900 € que parecían tuyos mirando solo ese mes, cierras el trimestre 5.000 € por debajo de como empezaste el año (los 2.100 € que ya faltaban más los 2.900 € que te llevas).',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.escenarios.2'),
                            'No es que marzo mienta. Es que marzo no es el año. El modelo 130 y el principal del préstamo cobran en acumulado, no en el buen humor del mes.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                        {t(k('p.escenarios.3Before'), 'Acción: ')}
                        <strong>
                            {t(
                                k('p.escenarios.3Strong'),
                                'antes de sacar, escribe el acumulado del año y la reserva de impuestos. Si no está en positivo, el mes bueno no autoriza extracción.',
                            )}
                        </strong>
                    </p>
                </div>

                <h2 id='semana' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                    {t(k('h2.semana'), 'Qué mirar antes de tocar la caja')}
                </h2>
                <ol className={articleUi.numListGap2xl}>
                    {weekItems.map(item => (
                        <li key={item} className={articleUi.numItem}>
                            {item}
                        </li>
                    ))}
                </ol>

                <h2 id='matices' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                    {t(k('h2.matices'), 'Lo que no cerramos nosotros')}
                </h2>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        <strong>
                            {t(
                                k('p.matices.1Strong'),
                                'Los tipos del Impuesto sobre Sociedades siguen en régimen transitorio.',
                            )}
                        </strong>{' '}
                        {t(
                            k('p.matices.1'),
                            'Bajan escalonadamente hasta 2029. No uses las cifras de 2026 como si fueran las de 2027 sin confirmarlas con tu asesor.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.matices.2'),
                            'El ejemplo del trimestre es nuestro, continuación del de marzo. Tu estacionalidad, tu deuda y tu régimen fiscal cambian los umbrales. No copies el 2.100 € ni el 5.000 € como si fueran los tuyos.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(k('p.matices.3Before'), 'Desde ')}
                        <TipiLink />
                        {t(
                            k('p.matices.3After'),
                            ' te ayudamos a ver el margen mes a mes. No hacemos asesoría fiscal ni te decimos cuánto sacar: eso lo cierras con tu gestoría cuando el acumulado esté claro.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.matices.4'),
                            'Si el mes te pide sacar y el año te pide esperar, cuéntanoslo y lo miramos.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                        {t(
                            k('closing.signoff'),
                            'Si sale algo nuevo sobre esto, lo cuentas aquí antes que en ningún sitio.',
                        )}
                    </p>
                </div>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿El mes dice sí y el año dice no?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Con Tipi ves la evolución del margen. La decisión de sacar la tomas tú con tu gestoría; nosotros te ayudamos a no decidir solo con el cajón de marzo.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.labelKey}>{t(k(source.labelKey), source.fallback)}</li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
